import { Injectable } from "@angular/core";
import { FeaturePermissionName } from "@common/ADAPT.Common.Model/embed/feature-permission-name.enum";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { ProgressService } from "@common/progress/progress.service";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { lastValueFrom } from "rxjs";

@Injectable({
    providedIn: "root",
})
export class JourneyProgressAuthService {
    public static readonly ReadJourneyProgress = "readJourneyProgress";
    public static readonly EditJourneyProgress = "editJourneyProgress";

    public static registerAccess(authorisationService: AuthorisationService) {
        authorisationService.registerAccessVerifier(
            JourneyProgressAuthService.ReadJourneyProgress,
            {
                invokeToGetAccessVerifier: (injector) =>
                    getVerifyAccessToReadJourneyProgress(
                        injector.get(CommonTeamsService),
                        injector.get(ProgressService),
                        injector.get(OrganisationService),
                    ),
            },
        );
        authorisationService.registerAccessVerifier(
            JourneyProgressAuthService.EditJourneyProgress,
            {
                invokeToGetAccessVerifier: (injector) =>
                    getVerifyAccessToEditJourneyProgress(
                        injector.get(CommonTeamsService),
                        injector.get(ProgressService),
                        injector.get(OrganisationService),
                    ),
            },
        );

        function getVerifyAccessToReadJourneyProgress(commonTeamsService: CommonTeamsService, progressService: ProgressService, organisationService: OrganisationService) {
            return async (currentPerson: Person) => canAccessJourneyProgress(currentPerson, commonTeamsService, progressService, organisationService, FeaturePermissionName.ProgressRead);
        }

        function getVerifyAccessToEditJourneyProgress(commonTeamsService: CommonTeamsService, progressService: ProgressService, organisationService: OrganisationService) {
            return async (currentPerson: Person) => canAccessJourneyProgress(currentPerson, commonTeamsService, progressService, organisationService, FeaturePermissionName.ProgressEdit);
        }

        async function canAccessJourneyProgress(currentPerson: Person, commonTeamsService: CommonTeamsService, progressService: ProgressService, organisationService: OrganisationService, permission: FeaturePermissionName) {
            // all team members have StrategyProgressRead/Edit, but only owner/leadership teams have the feature enabled.
            // we need to check team membership as well. since the entity isn't tied to a team, anyone with the team permission can read it.
            const teams = await commonTeamsService.promiseToGetActiveTeamsForPersonId(currentPerson.personId);
            const personHasAccess = teams.some((team) => authorisationService.personHasPermission(currentPerson, permission, team));
            if (personHasAccess || currentPerson.isCoach() || currentPerson.isStakeholderManager) {
                const products = await lastValueFrom(progressService.getProgressProducts(organisationService.getOrganisationId()));
                if (products.length > 0) {
                    return Promise.resolve();
                }
            }

            return Promise.reject();
        }
    }
}
